import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Save,
  Loader2,
  Images,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { logActivity } from '@/lib/logger';

interface Category {
  id: string;
  name: string;
}

export default function GalleryCategoryPage() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination logic
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.max(1, Math.ceil(categories.length / ITEMS_PER_PAGE));
  
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [categories.length, totalPages, currentPage]);

  const currentCategories = categories.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    const q = query(collection(db, 'gallery_categories'), orderBy('name'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const catData = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      })) as Category[];
      setCategories(catData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleOpenModal = (category: Category | null = null) => {
    if (category) {
      setEditingCategory(category);
      setNewCategoryName(category.name);
    } else {
      setEditingCategory(null);
      setNewCategoryName('');
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    try {
      if (editingCategory) {
        await updateDoc(doc(db, 'gallery_categories', editingCategory.id), {
          name: newCategoryName
        });
        await logActivity('edit', 'gallery', `Gallery Category: ${newCategoryName}`);
      } else {
        await addDoc(collection(db, 'gallery_categories'), {
          name: newCategoryName,
          createdAt: new Date().toISOString()
        });
        await logActivity('create', 'gallery', `Gallery Category: ${newCategoryName}`);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving gallery category:", error);
    }
  };

  const handleDelete = async (id: string) => {
    const category = categories.find(c => c.id === id);
    if (window.confirm(`Delete gallery category "${category?.name}"?`)) {
      await deleteDoc(doc(db, 'gallery_categories', id));
      if (category) {
        await logActivity('delete', 'gallery', `Gallery Category: ${category.name}`);
      }
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_gallery_cat_title')}</h1>
          <p className="text-white/50 text-sm">{t('admin_gallery_cat_sub')}</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-[#F57F25] hover:bg-[#e06d15] text-black font-black px-6 py-3 rounded-2xl flex items-center gap-2 transition-all uppercase text-sm shadow-lg shadow-[#F57F25]/20"
        >
          <Plus size={20} />
          {t('admin_gallery_cat_new')}
        </button>
      </div>

      <div className="bg-[#111] border border-white/5 rounded-[32px] overflow-hidden shadow-2xl">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#F57F25]" size={32} />
          </div>
        ) : categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center space-y-4">
            <Images size={48} className="text-white/10" />
            <p className="text-white/40 font-medium">{t('admin_gallery_cat_empty')}</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {currentCategories.map((cat) => (
              <div key={cat.id} className="flex items-center justify-between p-6 hover:bg-white/5 transition-colors group">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-white/20">
                    <Images size={20} />
                  </div>
                  <span className="text-white font-bold uppercase tracking-tight text-lg">{cat.name}</span>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => handleOpenModal(cat)}
                    className="p-3 text-blue-400 hover:bg-blue-400/10 rounded-xl transition-all"
                  >
                    <Pencil size={18} />
                  </button>
                  <button 
                    onClick={() => handleDelete(cat.id)}
                    className="p-3 text-red-400 hover:bg-red-400/10 rounded-xl transition-all"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {categories.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/5 bg-black/20">
            <p className="text-white/40 text-xs font-bold uppercase tracking-widest">
              {t('admin_pagination_page')} {currentPage} {t('admin_pagination_of')} {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 transition-colors"
                title={t('admin_pagination_prev')}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 transition-colors"
                title={t('admin_pagination_next')}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] w-full max-w-md rounded-[40px] border border-white/10 shadow-2xl overflow-hidden p-10">
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-2xl font-black text-white uppercase tracking-tight">
                {editingCategory ? t('admin_gallery_cat_edit') : t('admin_gallery_cat_new')}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-white/40 hover:text-white transition-colors">
                <X size={28} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-2">
                <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_gallery_cat_name')}</label>
                <input
                  type="text"
                  autoFocus
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-lg"
                  placeholder={t('admin_gallery_cat_placeholder')}
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#F57F25] hover:bg-[#e06d15] text-black font-black py-5 rounded-[20px] transition-all flex items-center justify-center gap-3 uppercase text-sm shadow-xl shadow-[#F57F25]/20"
              >
                <Save size={22} /> {t('admin_gallery_cat_save')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
